import { useNuxtApp, useProviderConfig } from '#imports';
import EvmMintFactory from '../../service/EVM/mint';
import { useUserStore } from '~/stores/user/useUserStore';
import { useAlertsStore } from '~/stores/alerts/useAlertsStore';
import { useTransactionStore } from '~/stores/transaction/useTransactionStore';
import type { Address } from 'viem';
import { formatCurrency } from '@/utils/helpers';

type UseMintProps = {
    slug: string;
    chainId: User['chainId'];
    mintInstance?: EvmMintFactory;
    address: Address;
};

export default function useMintClient({ slug, chainId, mintInstance, address }: UseMintProps) {
    const userStore = useUserStore();

    const { chainId: userChainId } = storeToRefs(userStore);

    const alertStore = useAlertsStore();
    const transactionStore = useTransactionStore();

    const { $provider } = useNuxtApp();

    const { eventEmitter } = useEVMCore();

    const {
        map: { chainCurrencyMap, chainMapById, chainMap }
    } = useProviderConfig()(['map', 'contract']);

    const initiatorKey = computed(() => `${slug}-${address}`);

    const mintService = mintInstance || new EvmMintFactory({
        slug,
        config: $provider.config,
        address
    });

    const { data: price, status: priceStatus } = useAsyncData(`${slug}-price`, async () => {
        const value = await mintService.getTokenPriceFromContract(chainId);

        return {
            value: formatCurrency({
                value,
                currencyType: chainCurrencyMap[chainId],
                unitType: 'wei'
            }),
            initialValue: value,
            currency: chainMapById[chainId].nativeCurrency.symbol
        };
    }, {
        watch: [userChainId],
        server: false,
        getCachedData: key => useNuxtApp().payload.data[key]
    });

    const { data: isActive, status: isActiveStatus } = useAsyncData(`${slug}-status`, async () => {
        return await mintService.checkSaleStatus();
    }, {
        watch: [userChainId],
        server: false,
        getCachedData: key => useNuxtApp().payload.data[key]
    });

    const mintTokens = async (params: { value: bigint; amount: number; network: User['network'] }) => {
        await eventEmitter.emit('mint', async () => {
            const transaction = await mintService.mint({ ...params, user: userStore.user, chainId: userStore.chainId });

            transactionStore.addTransaction({
                hash: transaction,
                chainId: chainMap[params.network].id,
                tokenList: [],
                value: 1,
                currency: params.network,
                status: 'pending',
                type: 'mint'
            });

            alertStore.add({
                title: 'Transaction submitted',
                description: 'Waiting for confirmation...',
                critical: false,
                status: 'type.info'
            });
        }, undefined, initiatorKey.value);
    };

    return {
        isLoading: computed(() => priceStatus.value === 'pending' || isActiveStatus.value === 'pending'),
        price,
        mintTokens,
        disabled: computed(() => (isActive.value !== undefined && !isActive.value) || priceStatus.value === 'error' || isActiveStatus.value === 'error')
    };
}
